import { Document, documentTable } from "models/schemas";
import { AtlasDocumentIngestion } from "pages/gaia-ai/definitions";
import { getFileNameWithoutExtension, toDateString } from "utils/date-format";

export const getSafeUrl = (url: string | null | undefined): string => {
  if (!url) return "https://";
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`;
  }
  return url;
};

export const getFileNameWithExtension = (doc: any): string => {
  return `${doc.fileName}${doc.extentionType}`;
};

export const getRelatedToLink = (
  parentId: string,
  category: string,
  gaiaObjectId: string
): string | null => {
  if (parentId.startsWith("TSK")) {
    return `/${getObjectTypeFromId(parentId)}/${parentId}`;
  } else if (getObjectTypeFromId(parentId).length !== 0) {
    return `/${
      category === "audits" ? "audit" : category
    }/${gaiaObjectId}/${getObjectTypeFromId(parentId)}/${parentId}`;
  }
  return null; // fallback for unknown types
};

export const getObjectTypeFromId = (id: string): string => {
  if (id.startsWith("TSK")) {
    return "Task";
  } else if (id.startsWith("payment")) {
    return "Payment";
  } else if (id.startsWith("assessment")) {
    return "Assessment";
  }
  return ""; // fallback for unknown types
};

export const shouldShowRelatedTo = (
  item: any,
  gaiaObjectId: string
): boolean => {
  // Don"t show for category headers (items with children) or items with same parentId
  return !item.children && item.parentId !== gaiaObjectId;
};

export const adaptAtlasDocumentToDocument = (
  atlasDoc: AtlasDocumentIngestion
): Document & { parsingStatus: string; ticketId?: string } => {
  const fileNameWithExtension = atlasDoc.documentUrl.split("/").pop() || "";
  const extentionType =
    "." + fileNameWithExtension.split(".").pop()?.toLowerCase() || "";

  return {
    documentId: atlasDoc.documentUrl,
    fileName: getFileNameWithoutExtension(fileNameWithExtension),
    documentType: "Atlas Document",
    extentionType: extentionType,
    size: atlasDoc.size,
    updatedBy: atlasDoc.createdBy,
    updatedAt: atlasDoc.createdAt?.toString(),
    createdBy: atlasDoc.createdBy,
    createdAt: atlasDoc.createdAt?.toString(),
    parsingStatus: atlasDoc.ingestionStatus || "Unknown",
    parentId: "",
    teamName: "",
    securityLevel: "Normal",
    deleted: false,
    s3Prefix: atlasDoc.documentUrl,
    externalUrl: undefined,
    ticketId: atlasDoc.ticketId,
  };
};

export const matchDateFilter = (
  itemValue: any,
  filterValue: any,
  operator: string
): boolean => {
  const numValue = Number(itemValue);
  const itemDateString = toDateString(numValue);

  let result: boolean;
  switch (operator) {
    case "=":
      result = itemDateString === filterValue;
      break;
    case "!=":
      result = itemDateString !== filterValue;
      break;
    case "<":
      result = itemDateString < filterValue;
      break;
    case "<=":
      result = itemDateString <= filterValue;
      break;
    case ">":
      result = itemDateString > filterValue;
      break;
    case ">=":
      result = itemDateString >= filterValue;
      break;
    default:
      result = false;
  }
  return result;
};
// Helper function to get all nested documents in a folder
export const getAllNestedDocuments = (
  parentId: string,
  allDocuments: Document[]
): Document[] => {
  const result: Document[] = [];
  const documentsByParent = new Map<string, Document[]>();

  // Pre-process documents by parentId for faster lookups
  allDocuments.forEach((doc) => {
    const docs = documentsByParent.get(doc.parentId) || [];
    docs.push(doc);
    documentsByParent.set(doc.parentId, docs);
  });

  const traverse = (currentId: string) => {
    const children = documentsByParent.get(currentId) || [];
    for (const child of children) {
      result.push(child);
      if (child.extentionType === "Folder") {
        traverse(`${child.parentId}/${child.fileName}`);
      }
    }
  };

  traverse(parentId);
  return result;
};

// Convert parentId to readable path for search
export const getReadablePath = (parentId: string, teamName: string): string => {
  if (!parentId || parentId === teamName) return "";

  const relativePath = parentId
    .replace(teamName, "")
    .split("/")
    .filter(Boolean);
  return relativePath.join(" > ");
};
